<template>
  <van-popup v-model:show="popupShow" destroy-on-close position="bottom" round teleport="body">
    <div class="pick-follow-up-data">
      <div class="pick-follow-up-data__head">
        <div
          class="pick-follow-up-data__head-action pick-follow-up-data__head-action--cancel"
          @click="popupShow = false"
        >
          取消
        </div>
        <div class="pick-follow-up-data__head-title">选择线索</div>
        <div
          class="pick-follow-up-data__head-action pick-follow-up-data__head-action--confirm"
          :class="{ 'is-disabled': !selectedItem.id }"
          @click="handleConfirm"
        >
          确定
        </div>
      </div>
      <div class="pick-follow-up-data__search">
        <van-search
          v-model="searchValue"
          class="pick-follow-up-data__search-box"
          placeholder="搜索线索"
          shape="round"
          @clear="handleSearch('')"
          @update:model-value="handleSearch"
        />
      </div>
      <div class="pick-follow-up-data__body">
        <van-list
          v-model:loading="loading"
          class="pick-follow-up-data__list"
          :finished="finished"
          finished-text="没有更多了"
          :immediate-check="false"
          @load="loadMore"
        >
          <template v-if="dataList.length">
            <div
              v-for="item in dataList"
              :key="item.id"
              class="pick-follow-up-data__item"
              @click="handleSelect(item)"
            >
              <div class="pick-follow-up-data__item-name">{{ item.name }}</div>
              <i
                v-if="selectedItem.id === item.id"
                class="icon-check-line pick-follow-up-data__item-check t-iconfont"
              ></i>
            </div>
          </template>
        </van-list>
        <div v-if="!loading && !dataList.length" class="pick-follow-up-data__empty">暂无线索</div>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { showToast } from 'vant'
import { getClueList } from '@/api/index.js'

const PAGE_SIZE = 20

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:show', 'confirm'])

const popupShow = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value)
})

const searchValue = ref('')
const dataList = ref([])
const selectedItem = ref({})
const loading = ref(false)
const finished = ref(false)
const page = ref(1)

let searchTimer = null

const resetListState = () => {
  dataList.value = []
  page.value = 1
  finished.value = false
  loading.value = false
}

const fetchList = async () => {
  if (finished.value) {
    loading.value = false
    return
  }

  loading.value = true
  try {
    const { result } = await getClueList({
      page: page.value,
      pageSize: PAGE_SIZE,
      nameLike: searchValue.value.trim()
    })
    const clueList = result?.clueList || []
    const rowsCount = result?.pageHelper?.rowsCount || 0
    dataList.value = [...dataList.value, ...clueList]
    page.value += 1
    finished.value = dataList.value.length >= rowsCount
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (finished.value) {
    return
  }
  await fetchList()
}

const handleSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    resetListState()
    loadMore()
  }, 300)
}

const handleSelect = (item) => {
  selectedItem.value = item
}

const handleConfirm = () => {
  if (!selectedItem.value.id) {
    showToast('请选择线索')
    return
  }
  emit('confirm', selectedItem.value)
  popupShow.value = false
}

watch(
  () => props.show,
  (value) => {
    if (value) {
      selectedItem.value = {}
      resetListState()
      loadMore()
      return
    }
    searchValue.value = ''
    selectedItem.value = {}
    clearTimeout(searchTimer)
  }
)
</script>

<style lang="scss" scoped>
.pick-follow-up-data {
  background: #fff;

  &__head {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 16px;
  }

  &__head-action {
    width: 52px;
    font-size: 14px;
    line-height: 20px;
  }

  &__head-action--cancel {
    color: #969799;
    text-align: left;
  }

  &__head-action--confirm {
    color: #7a52f4;
    text-align: right;
  }

  &__head-title {
    flex: 1;
    color: #323233;
    font-size: 16px;
    line-height: 22px;
    text-align: center;
  }

  &__search {
    border-bottom: 1px solid #f2f3f5;
  }

  &__search-box {
    :deep(.van-search) {
      padding: 0;
      background: transparent;
    }

    :deep(.van-search__content) {
      background: #f7f8fa;
      border-radius: 8px;
    }
  }

  &__body {
    height: 588px;
    overflow-y: auto;
  }

  &__list {
    min-height: 100%;
  }

  &__item {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 14px 28px;
    border-bottom: 1px solid #e5e6eb;
    box-sizing: border-box;
  }

  &__item-name {
    flex: 1;
    min-width: 0;
    color: #323233;
    font-size: 14px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-check {
    margin-left: 8px;
    color: #7a52f4;
    font-size: 20px;
    line-height: 20px;
    flex-shrink: 0;
  }

  &__empty {
    padding: 48px 16px;
    color: #969799;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }

  .is-disabled {
    color: #c396ed;
  }
}
</style>
